import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { studyPathAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, GraduationCap, Target, TrendingDown, BookOpen, 
  Zap, Clock, AlertTriangle, CheckCircle, Play
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

export default function StudyPath() {
  useSEO('Study Path | JepPrep', 'Follow a structured trivia study path tailored to your skill level and goals.');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sessionLength, setSessionLength] = useState('12');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [recRes, catRes] = await Promise.all([
        studyPathAPI.getRecommendations(),
        studyPathAPI.getCategories()
      ]);
      setRecommendations(recRes.data);
      setCategories(catRes.data.categories || []);
      
      // Auto-select weakest category if available
      if (recRes.data.recommendations?.length > 0) {
        setSelectedCategory(recRes.data.recommendations[0].category);
      }
    } catch (error) {
      toast.error('Failed to load study path data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async () => {
    if (!selectedCategory) {
      toast.error('Please select a category to practice');
      return;
    }

    setStarting(true);
    try {
      const response = await studyPathAPI.startSession({
        category: selectedCategory,
        num_questions: parseInt(sessionLength),
        include_review: true
      });
      
      navigate(`/study-path/play/${response.data.session_id}`);
    } catch (error) {
      toast.error('Failed to start study session');
      console.error(error);
    } finally {
      setStarting(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-rose-500';
      case 'medium': return 'bg-amber-500';
      case 'low': return 'bg-emerald-500';
      default: return 'bg-slate-500';
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'text-emerald-400';
    if (accuracy >= 60) return 'text-yellow-300';
    if (accuracy >= 40) return 'text-amber-400';
    return 'text-rose-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-white text-xl">Loading study path...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </Link>
            <img src={LOGO_URL} alt="JepPrep" className="h-8 sm:h-12 shrink-0" />
            <h1 className="text-base sm:text-xl font-display text-white flex items-center gap-2">
              <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
              Study Path
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {!recommendations?.has_recommendations ? (
          /* No Recommendations - Need Sprint Data */
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] p-4 sm:p-8 text-center max-w-2xl mx-auto">
            <div className="w-14 h-14 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-purple-500/20 flex items-center justify-center">
              <BookOpen className="w-7 h-7 sm:w-10 sm:h-10 text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Build Your Study Path</h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Complete Sprint sessions to help us identify your weak areas. 
              We need at least 15 questions per category to generate personalized recommendations.
            </p>
            <Link to="/sprint">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                <Zap className="w-4 h-4 mr-2" />
                Start Sprint Session
              </Button>
            </Link>
          </Card>
        ) : (
          /* Has Recommendations */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recommendations Panel */}
            <div className="lg:col-span-2 space-y-6">
              {/* Summary Card */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-400" />
                    Your Study Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
                    <div className="text-center p-2 sm:p-4 rounded-lg bg-[hsl(var(--accent))]">
                      <p className="text-xl sm:text-3xl font-bold text-rose-400">{recommendations.total_weak_categories}</p>
                      <p className="text-gray-400 text-xs sm:text-sm">Weak Areas</p>
                    </div>
                    <div className="text-center p-2 sm:p-4 rounded-lg bg-[hsl(var(--accent))]">
                      <p className="text-xl sm:text-3xl font-bold text-amber-400">
                        {recommendations.summary?.categories_needing_practice || 0}
                      </p>
                      <p className="text-gray-400 text-xs sm:text-sm">Need Practice</p>
                    </div>
                    <div className="text-center p-2 sm:p-4 rounded-lg bg-[hsl(var(--accent))]">
                      <p className="text-xl sm:text-3xl font-bold text-purple-400">
                        {recommendations.summary?.lowest_accuracy || 0}%
                      </p>
                      <p className="text-gray-400 text-xs sm:text-sm">Lowest Score</p>
                    </div>
                  </div>

                  {/* Recommended Categories */}
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-rose-400" />
                    Priority Categories
                  </h3>
                  <div className="space-y-3">
                    {recommendations.recommendations?.map((rec, idx) => (
                      <div 
                        key={rec.category}
                        onClick={() => setSelectedCategory(rec.category)}
                        className={`p-3 sm:p-4 rounded-lg cursor-pointer transition-all ${
                          selectedCategory === rec.category 
                            ? 'bg-purple-500/20 border border-purple-500/50' 
                            : 'bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/80'
                        }`}
                        data-testid={`category-${rec.category}`}
                      >
                        <div className="flex items-start sm:items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            {selectedCategory === rec.category && (
                              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 shrink-0" />
                            )}
                            <span className="text-white font-medium text-sm sm:text-base truncate">{rec.category}</span>
                            <Badge className={`${getPriorityColor(rec.priority)} text-white text-xs shrink-0`}>
                              {rec.priority}
                            </Badge>
                          </div>
                          <span className={`font-mono text-sm sm:text-base shrink-0 ${getAccuracyColor(rec.accuracy)}`}>
                            {rec.accuracy}%
                          </span>
                        </div>
                        <Progress 
                          value={rec.accuracy} 
                          className="h-2 bg-[hsl(var(--border))]"
                        />
                        <p className="text-gray-400 text-xs mt-2">
                          {rec.total_answered} answered • {rec.missed_questions_available} missed available
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* All Categories */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                    All Eligible Categories
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                    {categories.filter(c => c.eligible_for_study_path).map((cat, idx) => (
                      <div 
                        key={cat.name}
                        onClick={() => setSelectedCategory(cat.name)}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          selectedCategory === cat.name 
                            ? 'bg-purple-500/20 border border-purple-500/50' 
                            : 'bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/80'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {selectedCategory === cat.name && (
                            <CheckCircle className="w-4 h-4 text-purple-400 shrink-0" />
                          )}
                          <span className="text-white text-sm sm:text-base truncate">{cat.name}</span>
                          {cat.needs_improvement && (
                            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                          <span className="text-gray-400 text-xs sm:text-sm">{cat.total} Q</span>
                          <span className={`font-mono text-sm sm:text-base ${getAccuracyColor(cat.accuracy)}`}>
                            {cat.accuracy}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Session Config Panel */}
            <div className="space-y-6">
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] sticky top-8">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Play className="w-5 h-5 text-purple-400" />
                    Start Practice Session
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Selected Category */}
                  <div>
                    <Label className="text-gray-400 text-sm">Selected Category</Label>
                    <div className="mt-2 p-3 rounded-lg bg-[hsl(var(--accent))] border border-[hsl(var(--border))]">
                      {selectedCategory ? (
                        <span className="text-white font-medium">{selectedCategory}</span>
                      ) : (
                        <span className="text-gray-500">Click a category to select</span>
                      )}
                    </div>
                  </div>

                  {/* Session Length */}
                  <div>
                    <Label className="text-gray-400 text-sm mb-3 block">Session Length</Label>
                    <RadioGroup 
                      value={sessionLength} 
                      onValueChange={setSessionLength}
                      className="grid grid-cols-3 gap-2"
                    >
                      <div>
                        <RadioGroupItem value="12" id="length-12" className="peer sr-only" />
                        <Label 
                          htmlFor="length-12"
                          className="flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer
                            border-[hsl(var(--border))] peer-data-[state=checked]:border-purple-500 
                            peer-data-[state=checked]:bg-purple-500/20 hover:bg-[hsl(var(--accent))]"
                        >
                          <span className="text-white font-bold">12</span>
                          <span className="text-gray-400 text-xs">Quick</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="25" id="length-25" className="peer sr-only" />
                        <Label 
                          htmlFor="length-25"
                          className="flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer
                            border-[hsl(var(--border))] peer-data-[state=checked]:border-purple-500 
                            peer-data-[state=checked]:bg-purple-500/20 hover:bg-[hsl(var(--accent))]"
                        >
                          <span className="text-white font-bold">25</span>
                          <span className="text-gray-400 text-xs">Standard</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="50" id="length-50" className="peer sr-only" />
                        <Label 
                          htmlFor="length-50"
                          className="flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer
                            border-[hsl(var(--border))] peer-data-[state=checked]:border-purple-500 
                            peer-data-[state=checked]:bg-purple-500/20 hover:bg-[hsl(var(--accent))]"
                        >
                          <span className="text-white font-bold">50</span>
                          <span className="text-gray-400 text-xs">Deep Dive</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* What to Expect */}
                  <div className="p-4 rounded-lg bg-[hsl(var(--accent))] border border-[hsl(var(--border))]">
                    <h4 className="text-white font-medium mb-2 text-sm">What to Expect</h4>
                    <ul className="text-gray-400 text-xs space-y-1">
                      <li className="flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        ~{sessionLength === '12' ? '5' : sessionLength === '25' ? '10' : '20'} minutes
                      </li>
                      <li className="flex items-center gap-2">
                        <BookOpen className="w-3 h-3" />
                        Mix of new and review questions
                      </li>
                      <li className="flex items-center gap-2">
                        <Target className="w-3 h-3" />
                        Focus on your weak spots
                      </li>
                    </ul>
                  </div>

                  {/* Start Button */}
                  <Button
                    onClick={handleStartSession}
                    disabled={!selectedCategory || starting}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-6"
                    data-testid="start-study-session"
                  >
                    {starting ? (
                      'Starting...'
                    ) : (
                      <>
                        <Play className="w-5 h-5 mr-2" />
                        Start Practice
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Recent Sessions */}
              {recommendations.recent_sessions?.length > 0 && (
                <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                  <CardHeader>
                    <CardTitle className="text-white text-sm">Recent Sessions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {recommendations.recent_sessions.slice(0, 5).map((session, idx) => (
                        <div key={session.category || `rsession-${idx}`} className="flex justify-between items-center text-sm">
                          <span className="text-gray-300 truncate max-w-[120px]">{session.category}</span>
                          <span className={`font-mono ${getAccuracyColor(session.accuracy)}`}>
                            {session.accuracy}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
